import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import Storage from '../utils/storage';
import * as authService from '../services/api/auth';
import * as usersService from '../services/api/users';
import * as ordersService from '../services/api/orders';
import * as pickerService from '../services/api/picker';
import { transformUser } from '../services/transformers/userTransformer';
import { transformOrders } from '../services/transformers/orderTransformer';
import { getUserId, setUserId, removeUserId } from '../services/api/config';
import { checkUserAccess } from '../utils/userAccess';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pickups, setPickups] = useState([]);
  const [currentPickup, setCurrentPickup] = useState(null);
  const [pickupItems, setPickupItems] = useState([]);
  const [plasticRates, setPlasticRates] = useState({});

  useEffect(() => {
    loadUserData();
  }, []);

  // Note: Pickups will be loaded by individual pages (like Dashboard) when needed
  // This prevents unnecessary API calls when user is not on dashboard

  const loadUserData = async () => {
    setIsLoading(true);
    try {
      // First, try to load from localStorage immediately (for instant UI)
      const userData = await Storage.getItem('user');
      const authStatus = await Storage.getItem('isAuthenticated');
      const approvalStatus = await Storage.getItem('isApproved');
      const token = localStorage.getItem('auth_token');

      if (userData && token) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          setIsAuthenticated(authStatus === 'true');
          setIsApproved(approvalStatus === 'true');
        } catch (parseError) {
          console.error('Error parsing user data:', parseError);
        }
      }

      // Then, try to refresh from API if we have token and user ID
      const userId = getUserId();
      if (token && userId) {
        try {
          const result = await usersService.getUserById(userId);

          if (result.success && result.data) {
            const transformedUser = transformUser(result.data);
            setUser(transformedUser);
            setIsAuthenticated(true);
            setIsApproved(transformedUser.isApproved);

            // Store in localStorage for offline access
            await Storage.setItem('user', JSON.stringify(transformedUser));
            await Storage.setItem('isAuthenticated', 'true');
            await Storage.setItem('isApproved', transformedUser.isApproved.toString());
          }
        } catch (apiError) {
          console.error('Error fetching user from API:', apiError);
          // If API fails but we have cached data, keep using it
          // Only clear if we don't have cached data
          if (!userData) {
            // No cached data and API failed - user needs to login
            setUser(null);
            setIsAuthenticated(false);
            setIsApproved(false);
          }
        }
      } else if (!token && !userData) {
        // No token and no cached data - user is not logged in
        setUser(null);
        setIsAuthenticated(false);
        setIsApproved(false);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      // Try to load from localStorage as final fallback
      try {
        const userData = await Storage.getItem('user');
        const authStatus = await Storage.getItem('isAuthenticated');
        const approvalStatus = await Storage.getItem('isApproved');

        if (userData) {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          setIsAuthenticated(authStatus === 'true');
          setIsApproved(approvalStatus === 'true');
        } else {
          setUser(null);
          setIsAuthenticated(false);
          setIsApproved(false);
        }
      } catch (fallbackError) {
        console.error('Error loading user from localStorage:', fallbackError);
        setUser(null);
        setIsAuthenticated(false);
        setIsApproved(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Track if currently fetching to prevent concurrent calls
  const isLoadingPickupsRef = useRef(false);
  const lastPickupsFetchTimeRef = useRef(0);
  
  const PICKUPS_CACHE_TTL = 15 * 1000; // 15 seconds - short cache for real-time updates
  const PICKUPS_MIN_FETCH_INTERVAL = 10 * 1000; // Minimum 10 seconds between API calls
  const PICKUPS_CACHE_KEY = 'pickups_data_cache';
  
  // Check if cached pickups data is still valid
  const isPickupsCacheValid = useCallback((cachedData) => {
    if (!cachedData || !cachedData.timestamp) return false;
    const age = Date.now() - cachedData.timestamp;
    return age < PICKUPS_CACHE_TTL;
  }, []);
  
  // Check if enough time has passed since last fetch
  const canFetchPickups = useCallback(() => {
    const timeSinceLastFetch = Date.now() - lastPickupsFetchTimeRef.current;
    return timeSinceLastFetch >= PICKUPS_MIN_FETCH_INTERVAL;
  }, []);
  
  // Load pickups from cache
  const loadPickupsFromCache = useCallback(async () => {
    try {
      const cached = await Storage.getItem(PICKUPS_CACHE_KEY);
      if (cached) {
        const parsedCache = JSON.parse(cached);
        if (isPickupsCacheValid(parsedCache)) {
          return parsedCache.data;
        }
      }
      // Also check legacy cache format
      const legacyCache = await Storage.getItem('pickups');
      if (legacyCache) {
        const parsed = JSON.parse(legacyCache);
        // Ensure cache timestamp exists for tracking freshness
        if (Array.isArray(parsed) && parsed.length > 0 && !parsed[0]._cacheTimestamp) {
          const staleTimestamp = Date.now() - PICKUPS_CACHE_TTL; // Mark as potentially stale
          parsed.forEach(item => {
            item._cacheTimestamp = staleTimestamp;
          });
        }
        return parsed;
      }
    } catch (err) {
      console.warn('Error loading pickups cache:', err);
    }
    return null;
  }, [isPickupsCacheValid]);
  
  // Save pickups to cache
  const savePickupsToCache = useCallback(async (data) => {
    try {
      // Add cache timestamp to data for tracking freshness
      const dataWithTimestamp = Array.isArray(data) 
        ? data.map(item => ({ ...item, _cacheTimestamp: Date.now() })) 
        : { ...data, _cacheTimestamp: Date.now() };
      
      const cacheEntry = {
        data: dataWithTimestamp,
        timestamp: Date.now(),
      };
      await Storage.setItem(PICKUPS_CACHE_KEY, JSON.stringify(cacheEntry));
      // Also save in legacy format for backward compatibility
      await Storage.setItem('pickups', JSON.stringify(dataWithTimestamp));
    } catch (err) {
      console.warn('Error saving pickups cache:', err);
    }
  }, []);
  
  const loadPickups = useCallback(async (forceRefresh = false) => {
    // Prevent concurrent API calls
    if (isLoadingPickupsRef.current && !forceRefresh) {
      return;
    }

    // For real-time updates, we'll still check cache but with shorter TTL
    // This allows showing cached data instantly while fetching fresh data in background
    if (!forceRefresh) {
      const cachedData = await loadPickupsFromCache();
      if (cachedData) {
        // Update UI with cached data immediately for instant display
        setPickups(cachedData);
        // Check cache freshness - get timestamp from first item or cache entry
        let cacheTimestamp = 0;
        if (Array.isArray(cachedData) && cachedData.length > 0 && cachedData[0]._cacheTimestamp) {
          cacheTimestamp = cachedData[0]._cacheTimestamp;
        } else if (cachedData._cacheTimestamp) {
          cacheTimestamp = cachedData._cacheTimestamp;
        }
        
        // If cache is still fresh (less than 10 seconds old), skip API call
        const cacheAge = Date.now() - cacheTimestamp;
        if (cacheTimestamp > 0 && cacheAge < 10 * 1000) {
          lastPickupsFetchTimeRef.current = Date.now();
          return; // Cache is very fresh, skip API call
        }
        // Cache exists but is getting stale, continue to fetch fresh data
      }
    }

    // Check rate limiting (unless forcing refresh)
    if (!forceRefresh && !canFetchPickups()) {
      const cachedData = await loadPickupsFromCache();
      if (cachedData) {
        setPickups(cachedData);
        return;
      }
    }

    // No valid cache or force refresh - make API call
    isLoadingPickupsRef.current = true;
    lastPickupsFetchTimeRef.current = Date.now();

    try {
      // Fetch orders from picker orders API (no picker ID needed, uses auth token)
      const result = await pickerService.getPickerOrders();

      if (result.success && result.data && Array.isArray(result.data)) {
        // Transform API orders to app format
        const transformedOrders = transformOrders(result.data);
        // Add cache timestamp for tracking
        transformedOrders._cacheTimestamp = Date.now();
        setPickups(transformedOrders);
        await savePickupsToCache(transformedOrders);
      } else {
        console.warn('Failed to fetch orders from API, using cached data if available');
        // Try to load from cache
        const cachedData = await loadPickupsFromCache();
        if (cachedData) {
          setPickups(cachedData);
          return;
        }
        // No data available - set empty array
        setPickups([]);
      }
    } catch (error) {
      console.error('Error loading pickups:', error);
      // Try to load from cache on error
      const cachedData = await loadPickupsFromCache();
      if (cachedData) {
        setPickups(cachedData);
        return;
      }
      // No data available - set empty array
      setPickups([]);
    } finally {
      isLoadingPickupsRef.current = false;
    }
  }, [loadPickupsFromCache, savePickupsToCache, canFetchPickups]);

  const login = async (emailOrPhone, password) => {
    try {
      // The API expects email, but the UI allows emailOrPhone
      // For now, we'll treat it as email (backend should handle validation)
      const email = emailOrPhone.trim();

      const result = await authService.login(email, password);

      if (result.success && result.data?.user) {
        const apiUser = result.data.user;
        const transformedUser = transformUser(apiUser);

        // Check user access
        const access = checkUserAccess(transformedUser);

        // Even if access is denied, set user data (so error screens can display)
        setUser(transformedUser);
        setIsAuthenticated(true);
        // isApproved is based on ACTIVE status now, not APPROVED
        setIsApproved(access.hasAccess);

        // Store in localStorage for offline access
        await Storage.setItem('user', JSON.stringify(transformedUser));
        await Storage.setItem('isAuthenticated', 'true');
        await Storage.setItem('isApproved', access.hasAccess.toString());

        // Note: Pickups will be loaded by Dashboard component when user navigates there
        // This prevents unnecessary API calls if user is redirected elsewhere

        return { success: true, user: transformedUser, access };
      }

      return {
        success: false,
        error: result.message || 'Login failed. Please check your credentials.',
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.message || 'Login failed. Please try again.',
      };
    }
  };

  const signup = async (userData) => {
    try {
      // Call the signup API to create user account
      const signupResult = await authService.signup({
        name: userData.name,
        phone_number: userData.phone_number,
        email: userData.email,
        password: userData.password,
        photo: userData.photo || null,
        user_type: 'PICKER',
      });

      if (!signupResult.success) {
        return { 
          success: false, 
          error: signupResult.message || 'Registration failed' 
        };
      }

      // After successful signup, login to get full user data with status
      const loginResult = await login(userData.email, userData.password);

      if (loginResult.success && loginResult.user) {
        return { 
          success: true, 
          user: loginResult.user,
          access: loginResult.access
        };
      }

      // If login fails after signup, still consider signup successful
      // User can login manually
      return { 
        success: true, 
        user: null,
        access: { hasAccess: false, shouldShowPending: true }
      };
    } catch (error) {
      return { success: false, error: error.message || 'Registration failed' };
    }
  };

  const logout = async () => {
    try {
      // Call API logout endpoint
      await authService.logout();

      // Clear state
      setUser(null);
      setIsAuthenticated(false);
      setIsApproved(false);
      setCurrentPickup(null);
      setPickupItems([]);

      // Clear localStorage
      await Storage.multiRemove(['user', 'isAuthenticated', 'isApproved']);

      // Remove token and user ID (handled by authService.logout, but ensure it's done)
      removeUserId();
    } catch (error) {
      console.error('Error logging out:', error);
      // Even if API call fails, clear local state
      setUser(null);
      setIsAuthenticated(false);
      setIsApproved(false);
      setCurrentPickup(null);
      setPickupItems([]);
      await Storage.multiRemove(['user', 'isAuthenticated', 'isApproved']);
      removeUserId();
    }
  };

  const updateUser = async (updates) => {
    try {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      await Storage.setItem('user', JSON.stringify(updatedUser));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const addPickupItem = (item) => {
    setPickupItems([...pickupItems, item]);
  };

  const updatePickupItem = (itemId, updates) => {
    setPickupItems(pickupItems.map(item =>
      item.id === itemId ? { ...item, ...updates } : item
    ));
  };

  const removePickupItem = (itemId) => {
    setPickupItems(pickupItems.filter(item => item.id !== itemId));
  };

  const calculateTotal = () => {
    return pickupItems.reduce((total, item) => {
      const rate = plasticRates[item.category] || 0;
      return total + (item.weight * rate);
    }, 0);
  };

  const completePickup = async (orderId, accepted, reason = null) => {
    try {
      const updatedPickups = pickups.map(pickup => {
        if (pickup.id === orderId) {
          return {
            ...pickup,
            status: accepted ? 'COMPLETED' : 'DECLINED',
            items: accepted ? pickupItems : [],
            declinedReason: reason,
            completedDate: accepted ? new Date().toISOString() : null
          };
        }
        return pickup;
      });

      setPickups(updatedPickups);
      await Storage.setItem('pickups', JSON.stringify(updatedPickups));
      setPickupItems([]);
      setCurrentPickup(null);

      // Reload pickups from API to get latest data
      await loadPickups();

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const refreshPickups = async () => {
    await loadPickups();
  };

  const value = {
    user,
    isAuthenticated,
    isApproved,
    isLoading,
    pickups,
    currentPickup,
    pickupItems,
    plasticRates,
    setCurrentPickup,
    setPickupItems,
    login,
    signup,
    logout,
    updateUser,
    addPickupItem,
    updatePickupItem,
    removePickupItem,
    calculateTotal,
    completePickup,
    refreshPickups,
    loadPickups
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

